import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, Transition as _Transition } from "vue"

const _hoisted_1 = { class: "relative" }
const _hoisted_2 = { class: "pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 cursor-pointer" }
const _hoisted_3 = { key: 0 }
const _hoisted_4 = { class: "font-bold pl-3 mt-2" }
const _hoisted_5 = ["onClick"]
const _hoisted_6 = ["title"]
const _hoisted_7 = {
  key: 0,
  class: "absolute inset-y-0 right-0 flex items-center pr-4"
}
const _hoisted_8 = {
  key: 0,
  class: "text-gray-400"
}
const _hoisted_9 = ["onClick"]
const _hoisted_10 = { class: "block truncate font-normal" }
const _hoisted_11 = {
  key: 0,
  class: "absolute inset-y-0 right-0 flex items-center pr-4"
}

import {
  Combobox,
  ComboboxInput,
  ComboboxLabel,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/vue";
import { ChevronUpDownIcon, CheckIcon } from "@heroicons/vue/24/outline";
import { computed, watch, ref, PropType, onMounted } from "vue";
import { useI18n } from "vue-i18n";


export default /*#__PURE__*/_defineComponent({
  __name: 'ProcessDefaultComboboxFilter',
  props: {
  options: {
    type: Array as unknown as PropType<{ value: string; name: string }[]>,
  },
  groups: {
    type: Array as unknown as PropType<
      { name: string; field: string; options: { value: string; name: string }[] }[]
    >,
  },
  label: {
    type: String,
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  selected: {
    type: [String, Array, Object] as unknown as PropType<
      string | string[] | Record<string, string | string[]>
    >,
    default: "",
  },
  placeholder: {
    type: String,
    default: "",
  },
  isGantt: {
    type: Boolean,
  },
  class: {
    type: String,
    default: "",
  },
  defaultOpen: {
    type: Boolean,
    default: false,
  },
  dependencies: {
    type: Object as PropType<
      Record<
        string,
        {
          to: string;
          defineDependencies: (options: string[], value: string) => string[];
        }
      >
    >,
    default: () => ({}),
  },
},
  emits: ["update:selected"],
  setup(__props, { emit: __emit }) {

const { t } = useI18n();

const props = __props;

const mapDefaultSelectedProps = (value: unknown) => {
  if (props.options) {
    return new Set(value as string | string[]);
  }

  if (props.groups) {
    return Object.fromEntries(
      props.groups.map((group) => {
        return [group.field, new Set((value as Record<string, string | string[]>)[group.field])];
      }),
    );
  }
};

const emits = __emit;
const selected = ref(mapDefaultSelectedProps(props.selected));

const inputElement = ref<{ el: HTMLInputElement } | null>(null);

onMounted(() => {
  if (props.defaultOpen) {
    inputElement.value && openCombobox(inputElement.value.el);
  }
});

watch(
  () => props.selected,
  (value) => {
    selected.value = mapDefaultSelectedProps(value);
  },
);

const query = ref("");

const filteredOptions = computed(() => {
  if (query.value === "") {
    return props.options;
  }

  return props.options?.filter((option) => {
    return option.name.toLowerCase().includes(query.value.toLowerCase());
  });
});

const selectedCount = computed(() => {
  if (!selected.value) {
    return 0;
  }

  if ((selected.value as Set<string>).size) {
    return (selected.value as Set<string>).size;
  }

  const count = Object.entries(selected.value as Record<string, Set<string>>).reduce(
    (acc, [key, value]) => (props.dependencies[key] ? acc : (acc += value.size)),
    0,
  );

  return count || optionsCount.value;
});

const optionsCount = computed(() => {
  if (props.options) {
    return props.options.length;
  }

  return (props.groups || []).reduce(
    (acc, { field, options }) => (props.dependencies[field] ? acc : (acc += options.length)),
    0,
  );
});

const countDependencies = (field: string, value: string) => {
  if (!props.dependencies[field]) {
    return [0, 0];
  }

  const { to, defineDependencies } = props.dependencies[field];
  const options =
    props.groups?.find((group) => group.field === to)?.options?.map(({ value }) => value) || [];
  const dependencies = defineDependencies(options, value);

  const selectedItemsCount = dependencies.filter((dependency) =>
    (selected.value as Record<string, Set<string>>)[to].has(dependency),
  ).length;

  return [selectedItemsCount, dependencies.length];
};

const filteredGroups = computed(() => {
  if (query.value === "") {
    return props.groups;
  }

  return props.groups?.map((group) => ({
    ...group,
    options: group.options.filter((option) => {
      return option.name.toLowerCase().includes(query.value.toLowerCase());
    }),
  }));
});

const handleOptionChange = (value: string) => {
  const selects = selected.value as Set<string>;

  if (props.multiple) {
    if (selects.has(value)) {
      selects.delete(value);
    } else {
      selects.add(value);
    }
  } else {
    selected.value = new Set([value]);
  }

  const selectsArray = Array.from(selects);
  emits("update:selected", selectsArray);
};

const handleGroupsChange = (group: string, value: string) => {
  const selectedValue = selected.value as Record<string, Set<string>>;

  if (props.multiple) {
    const selectedInGroup = selectedValue[group];

    if (selectedInGroup.has(value)) {
      selectedInGroup.delete(value);
    } else {
      selectedInGroup.add(value);
    }
  } else {
    selectedValue[group] = new Set(value);
  }

  const selects = Object.fromEntries(
    Object.entries(selectedValue).map(([key, value]) => {
      return [key, Array.from(value)];
    }),
  );
  emits("update:selected", selects);
};

const openCombobox = (input: HTMLInputElement) => {
  input.dispatchEvent(new Event("input"));
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Combobox), {
    as: "div",
    style: {"min-width":"180px"},
    class: _normalizeClass(props.class),
    multiple: __props.multiple
  }, {
    default: _withCtx(() => [
      (__props.label)
        ? (_openBlock(), _createBlock(_unref(ComboboxLabel), { key: 0 }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(__props.label), 1)
            ]),
            _: 1
          }))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_1, [
        _createVNode(_unref(ComboboxInput), {
          class: "relative rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-gray-900 placeholder-gray-400 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-yellow-500 sm:text-sm text-xs sm:leading-6 w-full border-none",
          placeholder: 
          __props.placeholder && __props.isGantt
            ? `${_unref(t)(`${__props.placeholder}`)} (${selectedCount.value}/${optionsCount.value})`
            : _unref(t)('buttons.count_selected', { selected: selectedCount.value, total: optionsCount.value })
        ,
          onChange: _cache[0] || (_cache[0] = ($event: any) => (query.value = $event.target.value)),
          onClick: _cache[1] || (_cache[1] = ($event: any) => (openCombobox($event.target))),
          "aria-autocomplete": "one-time-code",
          autocomplete: "one-time-code",
          ref_key: "inputElement",
          ref: inputElement
        }, null, 8, ["placeholder"]),
        _createElementVNode("span", _hoisted_2, [
          _createVNode(_unref(ChevronUpDownIcon), {
            class: "h-5 w-5 text-gray-400",
            "aria-hidden": "true"
          })
        ]),
        _createVNode(_Transition, {
          "leave-active-class": "transition ease-in duration-100",
          "leave-from-class": "opacity-100",
          "leave-to-class": "opacity-0"
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(ComboboxOptions), { class: "absolute z-10 mt-1 max-h-60 overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-yellow-500/5 focus:outline-none text-xs sm:text-sm w-full" }, {
              default: _withCtx(() => [
                (props.groups)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList((filteredGroups.value as typeof groups), (group) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: group.field
                        }, [
                          _createElementVNode("p", _hoisted_4, _toDisplayString(group.name), 1),
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList((group.options as typeof options), (option) => {
                            return (_openBlock(), _createBlock(_unref(ComboboxOption), {
                              as: "template",
                              key: option.value,
                              value: option.value
                            }, {
                              default: _withCtx(({ active }) => [
                                _createElementVNode("li", {
                                  class: _normalizeClass([
                    active && 'bg-yellow-100',
                    'relative cursor-pointer select-none py-2 pl-5 pr-9 text-left',
                  ]),
                                  onClick: ($event: any) => (handleGroupsChange(group.field, option.value))
                                }, [
                                  _createElementVNode("span", {
                                    class: "block truncate font-normal",
                                    title: option.name
                                  }, _toDisplayString(option.name), 9, _hoisted_6),
                                  ((selected.value as Record<string, Set<string>>)[group.field]?.has?.(option.value) && __props.multiple)
                                    ? (_openBlock(), _createElementBlock("span", _hoisted_7, [
                                        (
                        __props.dependencies[group.field] &&
                        !countDependencies(group.field, option.value).every(
                          (v, _, arr) => v === arr[0],
                        )
                      )
                                          ? (_openBlock(), _createElementBlock("span", _hoisted_8, _toDisplayString(`${countDependencies(group.field, option.value).join("/")}`), 1))
                                          : (_openBlock(), _createBlock(_unref(CheckIcon), {
                                              key: 1,
                                              class: "h-5 w-5",
                                              "aria-hidden": "true"
                                            }))
                                      ]))
                                    : _createCommentVNode("", true)
                                ], 10, _hoisted_5)
                              ]),
                              _: 2
                            }, 1032, ["value"]))
                          }), 128))
                        ]))
                      }), 128))
                    ]))
                  : (_openBlock(true), _createElementBlock(_Fragment, { key: 1 }, _renderList((filteredOptions.value as typeof options), (option) => {
                      return (_openBlock(), _createBlock(_unref(ComboboxOption), {
                        as: "template",
                        key: option.value,
                        value: option.value
                      }, {
                        default: _withCtx(({ active }) => [
                          _createElementVNode("li", {
                            class: _normalizeClass([
                active && 'bg-yellow-100',
                'relative cursor-pointer select-none py-2 pl-5 pr-9 text-left',
              ]),
                            onClick: ($event: any) => (handleOptionChange(option.value))
                          }, [
                            _createElementVNode("span", _hoisted_10, _toDisplayString(option.name), 1),
                            ((selected.value as Set<string>)?.has?.(option.value) && __props.multiple)
                              ? (_openBlock(), _createElementBlock("span", _hoisted_11, [
                                  _createVNode(_unref(CheckIcon), {
                                    class: "h-5 w-5",
                                    "aria-hidden": "true"
                                  })
                                ]))
                              : _createCommentVNode("", true)
                          ], 10, _hoisted_9)
                        ]),
                        _: 2
                      }, 1032, ["value"]))
                    }), 128))
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ])
    ]),
    _: 1
  }, 8, ["class", "multiple"]))
}
}

})